import type { FC } from 'react';
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, MessageSquare, Plus, Trash2, ChevronDown, ArrowLeft } from 'lucide-react';
import type { ActionStatus, ActionItem as ActionItemType } from './ActionPlanSection';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Dropdown, DropdownItem } from '../ui/Dropdown';
import { HybridAvatar } from '../ui/Avatar';
import { useAuthStore } from '../../store/authStore';

// Mock data for current user - this would come from an auth context in a real app
const currentUser = {
  name: 'Você',
  avatar: '',
};

// --- Sub-components for better structure ---

interface ChecklistItemProps {
  item: { text: string; completed: boolean };
  onToggle: () => void;
  onDelete: () => void;
}

const ChecklistItem: FC<ChecklistItemProps> = ({ item, onToggle, onDelete }) => (
  <div className="flex items-center group py-1">
    <button onClick={onToggle} className="flex items-center flex-grow p-1 rounded hover:bg-neutral-100 transition-colors">
      <div className={`w-5 h-5 border-2 rounded-sm flex items-center justify-center mr-3 flex-shrink-0 ${item.completed ? 'bg-primary border-primary' : 'border-neutral-300'}`}>
        {item.completed && <Check size={16} className="text-white" />}
      </div>
      <span className={`flex-grow text-left ${item.completed ? 'line-through text-neutral-500' : 'text-neutral-800'}`}>{item.text}</span>
    </button>
    <button onClick={onDelete} className="opacity-0 group-hover:opacity-100 text-neutral-500 hover:text-red-500 transition-opacity ml-2">
      <Trash2 size={16} />
    </button>
  </div>
);

interface CommentProps {
  comment: { author: string; text: string; date: string; avatar: string; };
}

const Comment: FC<CommentProps> = ({ comment }) => (
  <div className="flex items-start space-x-3">
    <HybridAvatar name={comment.author} src={comment.avatar} size="sm" />
    <div className="flex-1">
      <div className="flex items-baseline space-x-2">
        <span className="font-bold text-neutral-800">{comment.author}</span>
        <span className="text-xs text-neutral-500">{new Date(comment.date).toLocaleString()}</span>
      </div>
      <p className="text-neutral-700 bg-neutral-100 p-3 rounded-lg mt-1">{comment.text}</p>
    </div>
  </div>
);


// --- Main ActionPlanDetail Component ---

interface ActionPlanDetailProps {
  action: ActionItemType;
  onClose: () => void;
  onChangeStatus: (actionId: string, newStatus: ActionStatus) => void;
}

export const ActionPlanDetail: FC<ActionPlanDetailProps> = ({ action, onClose, onChangeStatus }) => {
  const { user } = useAuthStore();
  
  // We'll manage the state locally. In a real app, this would be part of a global state manager or hooked to an API.
  const [checklist, setChecklist] = useState(action.checklist || []);
  const [comments, setComments] = useState(action.comments || []);

  const [newChecklistItem, setNewChecklistItem] = useState('');
  const [newComment, setNewComment] = useState('');

  // Estado para controlar a visão no mobile
  const [activeView, setActiveView] = useState<'details' | 'comments'>('details');
  const [direction, setDirection] = useState(1);

  const handleAddChecklistItem = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && newChecklistItem.trim()) {
      setChecklist([...checklist, { text: newChecklistItem.trim(), completed: false }]);
      setNewChecklistItem('');
    }
  };

  const handleToggleChecklistItem = (index: number) => {
    const updated = [...checklist];
    updated[index].completed = !updated[index].completed;
    setChecklist(updated);
  };

  const handleDeleteChecklistItem = (index: number) => {
    setChecklist(checklist.filter((_, i) => i !== index));
  };

  const handleAddComment = () => {
    if (newComment.trim()) {
      const commentToAdd = {
        author: user?.name || currentUser.name,
        avatar: user?.profile_image || user?.ProfileImageURL || '',
        text: newComment.trim(),
        date: new Date().toISOString(),
      };
      // New comments appear at the top
      setComments([commentToAdd, ...comments]);
      setNewComment('');
    }
  };

  const handleCommentKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAddComment();
    }
  };

  const checklistProgress = checklist.length > 0 ? (checklist.filter(i => i.completed).length / checklist.length) * 100 : 0;

  const panelVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? '100%' : '-100%',
      opacity: 0
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      zIndex: 0,
      x: direction < 0 ? '100%' : '-100%',
      opacity: 0
    })
  };

  const handleViewChange = (view: 'details' | 'comments') => {
    setDirection(view === 'comments' ? 1 : -1);
    setActiveView(view);
  };

  return (
    <div className="h-full">
      {/* --- Layout Desktop (visível em telas 'md' e maiores) --- */}
      <div className="hidden md:grid md:grid-cols-5 gap-x-4 h-full">
        {/* Coluna Principal (3/5) - com rolagem */}
        <div className="md:col-span-3 space-y-6 overflow-y-auto min-h-0 pl-4 pr-4 pb-4">
          {/* Description */}
          <div>
            <h3 className="text-lg font-semibold text-neutral-800 mb-2">Tarefa</h3>
            <p className="text-gray-700">{action.title}</p>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-neutral-800 mb-2">Descrição</h3>
            <p className="text-gray-700">{action.description}</p>
          </div>

          <hr />

          {/* Checklist Section */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-neutral-800 flex items-center">
                <Check size={20} className="mr-2" />
                Lista de Tarefas
              </h3>
              {checklist.length > 0 && (
                <span className="text-sm font-medium text-neutral-500">
                  {Math.round(checklistProgress)}% Concluído
                </span>
              )}
            </div>

            <Input
              type="text"
              placeholder="Adicionar um item..."
              value={newChecklistItem}
              onChange={(e) => setNewChecklistItem(e.target.value)}
              onKeyDown={handleAddChecklistItem}
              className="pl-8"
            />

            {checklist.length > 0 && (
              <div className="space-y-2">
                <div className="w-full bg-neutral-200 rounded-full h-2">
                  <div className="bg-green-500 h-2 rounded-full" style={{ width: `${checklistProgress}%` }}></div>
                </div>
                <div className="space-y-1 pt-2">
                  {checklist.map((item, index) => (
                    <ChecklistItem
                      key={index}
                      item={item}
                      onToggle={() => handleToggleChecklistItem(index)}
                      onDelete={() => handleDeleteChecklistItem(index)}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>

        </div>

        {/* Coluna Lateral (2/5) - com rolagem e destaque */}
        <div className="md:col-span-2 flex flex-col h-full bg-neutral-50 rounded-lg min-h-0">
          <div className="p-4">
            {/* Action Buttons */}
            <div className="flex items-center gap-x-4">
              <h4 className="text-sm font-semibold text-neutral-600 shrink-0">Ações do Card:</h4>
              <Dropdown
                align="left"
                trigger={
                  <Button variant="outline" size="sm">
                    <span>Mover para...</span>
                    <ChevronDown size={16} className="ml-2" />
                  </Button>
                }
              >
                <DropdownItem onClick={() => onChangeStatus(action.id, 'todo')}>Para Fazer</DropdownItem>
                <DropdownItem onClick={() => onChangeStatus(action.id, 'inprogress')}>Em Andamento</DropdownItem>
                <DropdownItem onClick={() => onChangeStatus(action.id, 'done')}>Concluído</DropdownItem>
              </Dropdown>
            </div>
          </div>

          <hr className="border-neutral-200" />

          {/* Comments Section - Scrolls independently */}
          <div className="flex-1 overflow-y-auto p-4 min-h-0">
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-neutral-800 flex items-center">
                <MessageSquare size={20} className="mr-2" />
                Comentários
              </h3>

              {/* New Comment Input */}
              <div className="flex items-start space-x-3 pt-2">
                <HybridAvatar name={user?.name || currentUser.name} src={user?.profile_image || user?.ProfileImageURL || currentUser.avatar} size="sm" className="mt-1" />
                <div className="flex-1">
                  <Input
                    placeholder="Escreva um comentário e pressione Enter"
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    onKeyDown={handleCommentKeyDown}
                    className="mb-2"
                  />
                </div>
              </div>

              {/* Existing Comments */}
              <div className="space-y-4 pt-4 border-t">
                {comments.map((comment, index) => <Comment key={index} comment={comment} />)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* --- Layout Mobile (visível apenas em telas pequenas) --- */}
      <div className="md:hidden relative h-full w-full overflow-hidden">
        <AnimatePresence initial={false} custom={direction}>
          <motion.div
            key={activeView}
            custom={direction}
            variants={panelVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{
              x: { type: "spring", stiffness: 300, damping: 30 },
              opacity: { duration: 0.2 }
            }}
            className="absolute top-0 left-0 w-full h-full bg-white"
          >
            {activeView === 'details' ? (
              // Painel de Detalhes
              <div className="h-full overflow-y-auto p-4 space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-neutral-800 mb-2">Tarefa</h3>
                  <p className="text-gray-700">{action.title}</p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-neutral-800 mb-2">Descrição</h3>
                  <p className="text-gray-700">{action.description}</p>
                </div>
                <hr />
                {/* Checklist Section */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-neutral-800 flex items-center">
                      <Check size={20} className="mr-2" />
                      Lista de Tarefas
                    </h3>
                    {checklist.length > 0 && (
                      <span className="text-sm font-medium text-neutral-500">
                        {Math.round(checklistProgress)}% Concluído
                      </span>
                    )}
                  </div>

                  <Input
                    type="text"
                    placeholder="Adicionar um item..."
                    value={newChecklistItem}
                    onChange={(e) => setNewChecklistItem(e.target.value)}
                    onKeyDown={handleAddChecklistItem}
                    className="pl-8"
                  />

                  {checklist.length > 0 && (
                    <div className="space-y-2">
                      <div className="w-full bg-neutral-200 rounded-full h-2">
                        <div className="bg-green-500 h-2 rounded-full" style={{ width: `${checklistProgress}%` }}></div>
                      </div>
                      <div className="space-y-1 pt-2">
                        {checklist.map((item, index) => (
                          <ChecklistItem
                            key={index}
                            item={item}
                            onToggle={() => handleToggleChecklistItem(index)}
                            onDelete={() => handleDeleteChecklistItem(index)}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              // Painel de Comentários
              <div className="flex flex-col h-full bg-neutral-50">
                <div className="p-4 flex-shrink-0 border-b">
                  <button onClick={() => handleViewChange('details')} className="flex items-center text-sm font-semibold text-neutral-600">
                    <ArrowLeft size={16} className="mr-2" />
                    Voltar para Detalhes
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto p-4 min-h-0">
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-neutral-800 flex items-center">
                      <MessageSquare size={20} className="mr-2" />
                      Comentários
                    </h3>

                    {/* New Comment Input for Mobile */}
                    <div className="flex items-start space-x-3 pt-2">
                      <HybridAvatar name={user?.name || currentUser.name} src={user?.profile_image || user?.ProfileImageURL || currentUser.avatar} size="sm" className="mt-1" />
                      <div className="flex-1">
                        <Input
                          placeholder="Escreva um comentário e pressione Enter"
                          value={newComment}
                          onChange={(e) => setNewComment(e.target.value)}
                          onKeyDown={handleCommentKeyDown}
                          className="mb-2"
                        />
                      </div>
                    </div>

                    {/* Existing Comments for Mobile */}
                    <div className="space-y-4 pt-4 border-t">
                      {comments.map((comment, index) => <Comment key={index} comment={comment} />)}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {/* Botão Flutuante para alternar as visões */}
        <div className="absolute bottom-4 right-4 z-20">
          <Button
            className="rounded-full shadow-lg w-14 h-14 bg-primary hover:bg-primary/90"
            onClick={() => {
              if (activeView === 'details') {
                handleViewChange('comments');
              } else if (activeView === 'comments' && newComment.trim()) {
                handleAddComment();
              } else {
                handleViewChange('details');
              }
            }}
          >
            {activeView === 'details' ? <MessageSquare /> : <Plus />}
          </Button>
        </div>
      </div>
    </div>
  );
}; 